import React, { useCallback, useEffect, useState } from 'react';
import {
  DomainListResponse,
  TenantDomain,
  domainService,
} from '../../../../services/domainService';
import { Button } from '../../../../components/ui/Button';
import { TextField } from '../../../../components/ui/Field';
import { Surface } from '../../../../components/ui/Surface';
import { Table } from '../../../../components/ui/Table';

interface DomainsTabProps {
  tenantId: string;
}

/**
 * Custom domains.
 *
 * Adding a host does nothing on its own — it produces a token the tenant
 * publishes as a DNS TXT record. Only a passing check makes the hostname resolve
 * to this tenant, which is what stops anyone pointing a CNAME at the platform and
 * inheriting a tenant. The UI leads with that, so the two steps are not mistaken
 * for one.
 */
const DomainsTab: React.FC<DomainsTabProps> = ({ tenantId }) => {
  const [data, setData] = useState<DomainListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [host, setHost] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await domainService.list(tenantId);
      setData(res as any);
    } catch {
      setMessage({ tone: 'error', text: 'Could not load domains.' });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!host.trim()) return;
    setAdding(true);
    setMessage(null);

    try {
      await domainService.add(tenantId, host.trim());
      setHost('');
      await load();
      setMessage({ tone: 'ok', text: 'Domain added. Publish the DNS records below, then verify.' });
    } catch (err: any) {
      setMessage({ tone: 'error', text: err?.details?.message || 'Could not add that domain.' });
    } finally {
      setAdding(false);
    }
  };

  const verify = async (domain: TenantDomain) => {
    setBusyId(domain.id);
    setMessage(null);

    try {
      await domainService.verify(tenantId, domain.id);
      await load();
      setMessage({ tone: 'ok', text: `${domain.host} verified.` });
    } catch (err: any) {
      setMessage({
        tone: 'error',
        text: err?.details?.message || 'Verification failed. DNS changes can take a few minutes.',
      });
    } finally {
      setBusyId(null);
    }
  };

  const makePrimary = async (domain: TenantDomain) => {
    setBusyId(domain.id);
    try {
      await domainService.setPrimary(tenantId, domain.id);
      await load();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err?.details?.error || 'Could not set the primary domain.' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (domain: TenantDomain) => {
    // Removing a verified domain stops it resolving, which signs its users out of
    // that hostname. Worth a confirmation.
    if (!window.confirm(`Remove ${domain.host}? Users on that hostname will no longer reach this tenant.`)) {
      return;
    }

    setBusyId(domain.id);
    try {
      await domainService.remove(tenantId, domain.id);
      await load();
    } catch {
      setMessage({ tone: 'error', text: 'Could not remove that domain.' });
    } finally {
      setBusyId(null);
    }
  };

  if (loading && !data) return <div className="text-agentic-text-secondary">Loading domains…</div>;

  const instructions = data?.instructions;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold">Domains</h2>
        <p className="mt-1 text-sm text-agentic-text-secondary">
          Hostnames that resolve to this tenant. A domain does nothing until it is verified —
          that check is what stops someone else's DNS pointing at this tenant.
        </p>
      </div>

      <section className="rounded-xl border bg-gray-50 p-6">
        <label className="text-sm font-medium text-gray-700">Add a domain</label>
        <div className="mt-2 flex gap-2">
          <TextField
            type="text"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="ai.client.edu"
            className="flex-1 rounded-lg border px-3 py-2 focus:ring-2 focus:ring-agentic-primary-blue"
          />
          <Button tone="primary"
            onClick={add}
            disabled={adding || !host.trim()}
            className="rounded-lg px-5 py-2 disabled:opacity-50"
          >
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Hostname only — no scheme, port or path. Subdomains of the platform domain are
          assigned automatically from the tenant slug and cannot be added here.
        </p>
      </section>

      {message && (
        <p
          className={`rounded-lg px-4 py-3 text-sm ${
            message.tone === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </p>
      )}

      <section className="space-y-4">
        {!data?.domains.length && (
          <p className="rounded-xl border border-dashed p-6 text-center text-sm text-agentic-text-secondary">
            No custom domains yet. This tenant is reachable on its platform subdomain.
          </p>
        )}

        {data?.domains.map((domain) => {
          const verified = Boolean(domain.verifiedAt);
          return (
            <Surface tone="plain" key={domain.id} className="rounded-xl border p-5">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-sm font-medium">{domain.host}</span>

                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    verified ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {verified ? 'Verified' : 'Pending verification'}
                </span>

                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{domain.kind}</span>

                {domain.isPrimary && (
                  <span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">Primary</span>
                )}

                <div className="ml-auto flex gap-2">
                  {!verified && (
                    <Button tone="custom"
                      onClick={() => verify(domain)}
                      disabled={busyId === domain.id}
                      className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50"
                    >
                      {busyId === domain.id ? 'Checking…' : 'Verify'}
                    </Button>
                  )}
                  {verified && !domain.isPrimary && (
                    <Button tone="custom"
                      onClick={() => makePrimary(domain)}
                      disabled={busyId === domain.id}
                      className="rounded-lg border px-3 py-1.5 text-sm disabled:opacity-50"
                    >
                      Make primary
                    </Button>
                  )}
                  <Button tone="custom"
                    onClick={() => remove(domain)}
                    disabled={busyId === domain.id}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-700 disabled:opacity-50"
                  >
                    Remove
                  </Button>
                </div>
              </div>

              {!verified && domain.verificationToken && instructions && (
                <div className="mt-4 space-y-3 rounded-lg bg-gray-50 p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Publish these DNS records, then press Verify
                  </p>

                  <div className="overflow-x-auto">
                    <Table className="w-full min-w-[520px] text-left text-xs">
                      <thead className="text-gray-500">
                        <tr>
                          <th className="py-1 pr-4 font-medium">Type</th>
                          <th className="py-1 pr-4 font-medium">Name</th>
                          <th className="py-1 font-medium">Value</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        <tr className="border-t">
                          <td className="py-2 pr-4">TXT</td>
                          <td className="py-2 pr-4 break-all">
                            {instructions.verificationHost}.{domain.host}
                          </td>
                          <td className="py-2 break-all">{domain.verificationToken}</td>
                        </tr>
                        {instructions.cnameTarget && (
                          <tr className="border-t">
                            <td className="py-2 pr-4">CNAME</td>
                            <td className="py-2 pr-4 break-all">{domain.host}</td>
                            <td className="py-2 break-all">{instructions.cnameTarget}</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {!instructions.cnameTarget && (
                    <p className="text-xs text-gray-500">
                      No CNAME target is configured on the platform (PLATFORM_CNAME_TARGET), so only the
                      ownership record is shown. Point the host at the platform's ingress separately.
                    </p>
                  )}

                  {domain.lastCheckedAt && (
                    <p className="text-xs text-gray-500">
                      Last checked {new Date(domain.lastCheckedAt).toLocaleString()}.
                    </p>
                  )}
                </div>
              )}

              {verified && (
                <p className="mt-3 text-xs text-gray-500">
                  Verified {new Date(domain.verifiedAt as string).toLocaleString()} · TLS: {domain.tlsStatus}
                </p>
              )}
            </Surface>
          );
        })}
      </section>
    </div>
  );
};

export default DomainsTab;
